import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIconProps, FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import styles from './styles.module.scss';
import PurifyComponent from '../utils/PurifyComponent/index.ts';
import addVariants from '../utils/addVariants/index.ts';

const IconComponent = PurifyComponent<FontAwesomeIconProps>(FontAwesomeIcon);
export const ICON_SIZES = [
  '1x',
  '2x',
  '3x',
  '4x',
  '5x',
  '6x',
  '7x',
  '8x',
  '9x',
  '10x',
  'xs',
  'lg',
  'sm'
] as const;
export const ICON_FLIP_TYPES = ['horizontal', 'vertical', 'both'] as const;
export const ICON_PULL_DIRECTIONS = ['left', 'right'] as const;
export const ICON_DIRECTIONS = [90, 180, 270] as const;

export interface IconProps extends Omit<FontAwesomeIconProps, 'icon' | 'mask'> {
  icon: string[] | { iconName: string; prefix: string };
  mask?: string[] | { iconName: string; prefix: string };
}

const Icon = ({ className, fixedWidth, icon, mask, ...props }: IconProps): JSX.Element => {
  const newClassName = useMemo(
    () => classnames(className, addVariants('aglmd-icon', styles, fixedWidth && 'fw')),
    [className, fixedWidth]
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const typedIcon: IconProp = icon as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const typedMask: IconProp = mask as any;

  return (
    <IconComponent
      className={newClassName}
      fixedWidth={fixedWidth}
      icon={typedIcon}
      mask={typedMask}
      {...props}
    />
  );
};

Icon.propTypes = {
  /**
   * Specify which icon to use
   */
  icon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      prefix: PropTypes.string.isRequired
    })
  ]).isRequired,
  /**
   * Set the size of the icon
   */
  size: PropTypes.oneOf(ICON_SIZES),
  /**
   * Class to set on the icon
   */
  className: PropTypes.string,
  /**
   * Whether to spin the icon
   */
  spin: PropTypes.bool,
  /**
   * Whether to pulse the icon
   */
  pulse: PropTypes.bool,
  /**
   * Show border radius or not
   */
  border: PropTypes.bool,
  /**
   * Make icon fixed width (normally depends on the icon)
   */
  fixedWidth: PropTypes.bool,
  /**
   * Invert the icon
   */
  inverse: PropTypes.bool,
  /**
   * Flip the icon in one of the directions
   */
  flip: PropTypes.oneOf(ICON_FLIP_TYPES),
  /**
   * Pull the icon left or right
   */
  pull: PropTypes.oneOf(ICON_PULL_DIRECTIONS),
  /**
   * The degrees to rotate the icon by
   */
  rotation: PropTypes.oneOf(ICON_DIRECTIONS),
  /**
   * Inline styles to apply to the icon
   */
  style: PropTypes.object
};

Icon.defaultProps = {
  fixedWidth: true
};

export default Icon;
